import { css } from '@emotion/react';
import { Fragment } from 'react';
import Title from 'components/Title/Title';
import Page from 'components/Page/Page';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import ErrorScreen from 'components/ErrorScreen/ErrorScreen';

import StateBadge from './StateBadge/StateBadge';
import TaskGraph from './TaskGraph/TaskGraph';
import ExpandableDetails from './ExpandableDetails/ExpandableDetails';
import JsonViewer from './JsonViewer/JsonViewer';
import TaskDetails from './TaskDetails/TaskDetails';

import Paper from '@mui/material/Paper';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import bulkResetFailedTasks from 'store/bulkResetFailedTasks';
import bulkResetOrphanedTasks from 'store/bulkResetOrphanedTasks';
import fetchRunDetails from 'store/fetchRunDetails';
import useAutofetchedRunDetailsAsync from 'store/useAutofetchedRunDetailsAsync';
import useAutofetchedWorkflowProgressAsync from 'store/useAutofetchedWorkflowProgressAsync';

import { unique } from 'utils/array';

import RunState, { supportedRunStates } from 'types/RunState';

export default function WorkflowRunDetailsPage() {
  const { runId } = useParams() as { runId: string };
  const runDetailsAsync = useAutofetchedRunDetailsAsync(runId);
  const workflowProgressAsync = useAutofetchedWorkflowProgressAsync(runId);

  const [expandWorkflowDetails, setExpandWorkflowDetails] = useState(true);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);

  useEffect(() => {
    setExpandWorkflowDetails(selectedTaskIds.length === 0);
  }, [selectedTaskIds]);

  const [columnMode, setColumnMode] = useState<'DAG' | 'DETAILS' | 'BOTH'>('BOTH');
  const [resettingAll, setResettingAll] = useState(false);
  const [resettingOrphaned, setResettingOrphaned] = useState(false);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  switch (runDetailsAsync.status) {
    case 'INIT':
    case 'PENDING':
      return <LoadingScreen />;
    case 'ERROR':
      return <ErrorScreen />;
    case 'SUCCESS':
    case 'REFRESH': {
      const runDetails = runDetailsAsync.data;

      // Drive the legend from the polled progress map when it has landed, so
      // no-longer-present states (e.g. RUNNING after everything completes)
      // drop out of the legend without a page reload. Fall back to the
      // one-shot runDetails snapshot while the first progress fetch is in
      // flight.
      const liveTaskStates: RunState[] = workflowProgressAsync.data?.tasks
        ? Object.values(workflowProgressAsync.data.tasks).map((t) => t.state)
        : Object.values(runDetails.task_states);

      const usedRunStates = liveTaskStates
        .filter(unique)
        .sort((a, b) => supportedRunStates.indexOf(a) - supportedRunStates.indexOf(b));

      return (
        <Fragment>
          <Title>{`${runDetails.name} | Tailor`}</Title>

          <Page>
            <div
              css={css`
                margin: 48px;
                margin-top: 24px;

                display: flex;
                flex-direction: column;
                flex-grow: 1;
              `}
            >
              <div
                css={css`
                  display: flex;
                `}
              >
                <ToggleButtonGroup
                  exclusive
                  value={columnMode}
                  onChange={(e, mode) =>
                    // Don't change mode to null (we want exactly one selected at all times)
                    mode && setColumnMode(mode)
                  }
                  aria-label="View mode"
                  css={css`
                    button {
                      padding: 4px 24px;
                    }
                  `}
                >
                  <ToggleButton value="DAG" aria-label="Show only DAG" title="Show only DAG">
                    DAG
                  </ToggleButton>
                  <ToggleButton value="BOTH" aria-label="Show both DAG and Details" title="Show both DAG and Details">
                    Both
                  </ToggleButton>
                  <ToggleButton value="DETAILS" aria-label="Show only Details" title="Show only Details">
                    Details
                  </ToggleButton>
                </ToggleButtonGroup>
                {runDetails.worker_lost_task_ids?.length > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    disabled={resettingOrphaned}
                    onClick={() => {
                      setResettingOrphaned(true);
                      bulkResetOrphanedTasks(runDetails.project_id, runDetails.id)
                        .then((result) => {
                          setResetMessage(
                            result.recovered_count > 0
                              ? `Reset ${result.recovered_count} lost task(s)`
                              : 'No lost tasks to reset',
                          );
                          fetchRunDetails(runId);
                        })
                        .catch((err) => {
                          console.error('Lost task reset failed:', err);
                          setResetMessage('Reset failed');
                        })
                        .finally(() => setResettingOrphaned(false));
                    }}
                    sx={{ ml: 2 }}
                  >
                    {resettingOrphaned && <CircularProgress size={16} sx={{ mr: 1 }} />}
                    {resettingOrphaned ? 'Resetting\u2026' : 'Reset lost tasks'}
                  </Button>
                )}
                {liveTaskStates.includes('FAILED' as RunState) && (
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={resettingAll}
                    onClick={() => {
                      setResettingAll(true);
                      bulkResetFailedTasks(runDetails.project_id, runDetails.id)
                        .then((result) => {
                          setResetMessage(
                            result.recovered_count > 0
                              ? `Reset ${result.recovered_count} failed task(s)`
                              : 'No failed tasks to reset',
                          );
                          fetchRunDetails(runId);
                        })
                        .catch((err) => {
                          console.error('Bulk reset failed:', err);
                          setResetMessage('Reset failed');
                        })
                        .finally(() => setResettingAll(false));
                    }}
                    sx={{ ml: 2 }}
                  >
                    {resettingAll && <CircularProgress size={16} sx={{ mr: 1 }} />}
                    {resettingAll ? 'Resetting\u2026' : 'Reset All Failed Tasks'}
                  </Button>
                )}
              </div>
              <div
                css={css`
                  margin-top: 24px;

                  @media (max-width: 767px) {
                    margin: 12px;
                  }

                  flex-grow: 1;
                  display: flex;
                  grid-gap: 24px;

                  > *:first-of-type {
                    // Make graph column sticky, and prevent it from growing beyond
                    // filling the space between header and footer.
                    max-height: calc(100vh - 276px);
                    overflow: auto;
                    position: sticky;
                    top: 147px;
                  }

                  > * {
                    // Control width of the two columns (max 100vw - vertical margins)
                    min-width: min(calc(100vw - 2 * 48px), 580px);
                    // width 0 prevents content from inflating the flex basis.
                    width: 0;
                  }

                  > *:first-of-type {
                    flex-grow: 3;
                  }

                  > *:nth-of-type(2) {
                    flex-grow: 1;
                  }

                  @media (max-width: 1280px /* 1280 = 2*580 + 2*48 + 24 – Max width with 2 columns */) {
                    flex-direction: column;

                    > *:first-of-type {
                      position: unset;
                      max-height: unset;
                    }
                    > * {
                      width: unset;
                    }
                  }
                `}
              >
                {['DAG', 'BOTH'].includes(columnMode) && (
                  <Paper
                    elevation={2}
                    css={css`
                      flex-grow: 1;
                      display: flex;
                      flex-direction: column;
                    `}
                  >
                    <TaskGraph
                      runDetails={runDetails}
                      setSelectedTaskIds={setSelectedTaskIds}
                      progressByTaskId={workflowProgressAsync.data?.tasks}
                    />
                    <div
                      css={css`
                        padding: 24px;
                        display: flex;
                        flex-wrap: wrap;
                        grid-gap: 12px;
                      `}
                    >
                      {usedRunStates.map((state: RunState) => (
                        <StateBadge key={state} state={state} />
                      ))}
                      {runDetails.worker_lost_task_ids?.length > 0 && (
                        <span
                          css={css`
                            display: inline-block;
                            padding: 8px;
                            font-size: 21px;
                            line-height: 18px;
                            letter-spacing: 0.08em;
                            color: #333;
                            background: #fff;
                            border: 3px dashed #c62828;
                          `}
                        >
                          WORKER LOST
                        </span>
                      )}
                    </div>
                  </Paper>
                )}

                {['DETAILS', 'BOTH'].includes(columnMode) && (
                  <div>
                    <Accordion expanded={expandWorkflowDetails} onChange={() => setExpandWorkflowDetails((on) => !on)}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="run-details-content"
                        id="run-details-header"
                      >
                        <div
                          css={css`
                            padding: 0 8px 0 12px;
                            flex-grow: 1;
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                          `}
                        >
                          <Typography component="h1" variant="h6">
                            {runDetails.id} {runDetails.name}
                          </Typography>
                          <Typography component="h1" variant="h6" style={{ fontSize: 10, letterSpacing: 1.5 }}>
                            WORKFLOW
                          </Typography>
                        </div>
                      </AccordionSummary>
                      <AccordionDetails style={{ display: 'block' }}>
                        <ExpandableDetails
                          summary="Inputs"
                          contentPlaceholder="Inputs as json (only rendered when open)"
                          renderContent={() => <JsonViewer json={runDetails.inputs} />}
                        />
                        <ExpandableDetails
                          summary="Outputs"
                          contentPlaceholder="Outputs as json (only rendered when open)"
                          renderContent={() => <JsonViewer json={runDetails.outputs} />}
                        />
                        <ExpandableDetails
                          summary="Files"
                          contentPlaceholder="Files as json (only rendered when open)"
                          renderContent={() => <JsonViewer json={runDetails.files} />}
                        />
                        <ExpandableDetails
                          summary="Local files"
                          contentPlaceholder="Local files as json (only rendered when open)"
                          renderContent={() => <JsonViewer json={runDetails.local_files ?? {}} />}
                        />
                        <ExpandableDetails
                          summary="DAG"
                          contentPlaceholder="DAG as json (only rendered when open)"
                          renderContent={() => <JsonViewer json={runDetails.dag} />}
                        />
                      </AccordionDetails>
                    </Accordion>

                    {selectedTaskIds
                      .filter(
                        (id) =>
                          !id.startsWith('summary:') && !id.startsWith('page-prev:') && !id.startsWith('page-next:'),
                      )
                      .map((taskId) => (
                        <TaskDetails key={taskId} runId={runId} taskId={taskId} />
                      ))}
                  </div>
                )}
              </div>
            </div>
          </Page>
          <Snackbar
            open={resetMessage !== null}
            autoHideDuration={4000}
            onClose={() => setResetMessage(null)}
            message={resetMessage}
          />
        </Fragment>
      );
    }
  }
}
